import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { useClickOutside } from "hooks/useClickOutside";
import { useChallengesState } from "hooks/useChallengesState";

const SelectActions = ({ category_id, instance_id, draft_id, item_id, item_type }) => {
  const { state, addDraftAction, updateDraftAction, deleteDraftAction } = useChallengesState();
  const [isExpanded, setIsExpanded] = useState(false);
  const [newActionName, setNewActionName] = useState("");
  const dropdownRef = useRef(null);
  
  useClickOutside(dropdownRef, () => setIsExpanded(false));

  const draft = state.challenges.categories[category_id]?.challengeDrafts[draft_id];
  const item = item_type === 'opportunities' 
    ? draft?.opportunities?.find(o => o.id === item_id)
    : draft?.risks?.find(r => r.id === item_id);

  const actions = item?.actions || [];

  const [showCustomOutcome, setShowCustomOutcome] = useState({});
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const latestInputRef = useRef(null);

  useEffect(() => {
    if (latestInputRef.current) {
      latestInputRef.current.focus();
    }
  }, [actions.length]);

  const addNewRow = () => {
    addDraftAction(category_id, draft_id, item_type, item_id, {
      id: `action-${item_type}-${item_id}-${Date.now()}`,
      metrics: {
        unit: '#',
        target: '',
        actual: ''
      }
    });
  };

  const deleteRow = (actionId) => {
    setShowCustomOutcome(prev => {
      const { [actionId]: removed, ...rest } = prev;
      return rest;
    });
    deleteDraftAction(category_id, draft_id, item_type, item_id, actionId);
  };

  const updateAction = (actionId, updates) => {
    if (updates.metrics && !updates.metrics.unit) {
      const existingAction = actions.find(a => a.id === actionId);
      updates.metrics.unit = existingAction?.metrics?.unit || '#';
    }
    updateDraftAction(category_id, draft_id, item_type, item_id, actionId, updates);
  };

  // Don't render if no item is selected
  if (!item) {
    return null;
  }

  return (
    <Actions>
      {actions.map((action, index) => (
        <ActionRow 
          key={action.id}
          onMouseEnter={() => setHoveredIndex(index)}
          onMouseLeave={() => setHoveredIndex(null)}
        >
          <Badge 
            onClick={() => actions.length > 1 && deleteRow(action.id)}
            isHovered={hoveredIndex === index && actions.length > 1}
          >
            {hoveredIndex === index && actions.length > 1 ? '×' : index + 1}
          </Badge>
          <ActionInput 
            ref={index === actions.length - 1 ? latestInputRef : null}
            placeholder="What needs to be done?" 
            value={action.content || ''}
            onChange={(e) => updateAction(action.id, { content: e.target.value })}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addNewRow();
              }
            }}
          />
          <Arrow>→</Arrow>
          <OutcomeWrapper>
            {showCustomOutcome[action.id] ? (
              <OutcomeInput 
                placeholder="Type custom outcome..." 
                value={action.outcome || ''}
                onChange={(e) => updateAction(action.id, { outcome: e.target.value })}
              />
            ) : (
              <OutcomeSelect
                value={action.outcome || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setShowCustomOutcome(prev => ({
                    ...prev,
                    [action.id]: value === "other"
                  }));
                  if (value !== "other") {
                    updateAction(action.id, { outcome: value });
                  }
                }}
                aria-label="Select outcome..."
              >
                <option value="">Select outcome...</option>
                <option value="increase">Increase Revenue</option>
                <option value="decrease">Decrease Costs</option>
                <option value="improve">Improve Efficiency</option>
                <option value="other">Other...</option>
              </OutcomeSelect>
            )}
          </OutcomeWrapper>
          <MetricWrapper>
            <Metric>
              <MetricPre 
                value={action.metrics?.unit || '#'}
                onChange={(e) => updateAction(action.id, { 
                  metrics: { 
                    ...action.metrics, 
                    unit: e.target.value 
                  } 
                })}
                data-testid="metric-unit-select"
              >
                <option value="#">#</option>
                <option value="$">$</option>
                <option value="%">%</option>
              </MetricPre>
              <MetricBoxwithPre 
                placeholder="Target"
                inputMode="numeric"
                pattern="[0-9]*"
                value={action.metrics?.target || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  updateAction(action.id, { 
                    metrics: { 
                      ...action.metrics, 
                      target: value
                    } 
                  });
                }}
                onKeyDown={(e) => {
                  if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                data-testid="metric-target-input"
              />
            </Metric>
            <Metric>
              <MetricBox 
                placeholder="Actual"
                inputMode="numeric"
                pattern="[0-9]*"
                value={action.metrics?.actual || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  updateAction(action.id, { 
                    metrics: { 
                      ...action.metrics, 
                      actual: value
                    } 
                  });
                }}
                onKeyDown={(e) => {
                  if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                data-testid="metric-actual-input"
              />
            </Metric>
          </MetricWrapper>
        </ActionRow>
      ))}
      <AddActionButton onClick={addNewRow}>
        Add Action ?
      </AddActionButton>
    </Actions>
  );
};

const Actions = styled.div.attrs({
  className: "Actions",
})`
  padding: 15px 10px;
  background: #1e1e1e;
  border-radius: 0 0 3px 3px;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const ActionRow = styled.div.attrs({
  className: "ActionRow",
})`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 0px;
`;

const ActionInput = styled.input.attrs({
  className: "ActionInput",
})`
  flex: 2;
  border: none;
  outline: none;
  background: #2e2e2e;
  color: white;
  padding: 8px;
  border-radius: 2px;
  font-size: 13px;
  box-sizing: border-box;
  font-size: 12px;
  font-family: pt sans;
`;

const OutcomeWrapper = styled.div.attrs({
  className: "OutcomeWrapper",
})`
  position: relative;
  display: flex;
  flex: 1.5;
`;

const OutcomeSelect = styled.select.attrs({
  className: "OutcomeSelect",
})`
  border: none;
  outline: none;
  background: #2e2e2e;
  color: white;
  padding: 8px;
  border-radius: 2px;
  font-size: 12px;
  font-family: pt sans;
  appearance: none;
  cursor: pointer;
  flex: 1;
  display: flex;
  &::-ms-expand {
    display: none;
  }
`;

const OutcomeInput = styled.input.attrs({
  className: "OutcomeInput",
})`
  width: 100%;
  border: none;
  outline: none;
  background: #2e2e2e;
  color: white;
  padding: 8px;
  border-radius: 2px;
  font-size: 13px;
  box-sizing: border-box;
  width: 100%;
`;

const MetricWrapper = styled.div.attrs({
  className: "MetricWrapper",
})`
  flex: 1;
  display: flex;
  gap: 8px;
`;

const Metric = styled.div.attrs({
  className: "Metric",
})`
  display: flex;
  flex-direction: row;
`;

const MetricBox = styled.input.attrs({
  className: "MetricBox",
})`
  border: none;
  outline: none;
  background: #2e2e2e;
  color: white;
  padding: 8px 5px;
  border-radius: 2px;
  font-size: 12px;
  font-family: pt sans;
  text-align: center;
  min-width: 40px;
  max-width: 60px;
  background: white;
  color: black;
  font-weight: 700;
  &::placeholder {
    text-align: center;
  }
`;

const MetricBoxwithPre = styled(MetricBox).attrs({
  className: "MetricsBoxwithPre",
})`
  border-radius: 0px 3px 3px 0px;
`;

const MetricPre = styled.select.attrs({
  className: "MetricPre",
})`
  background: #2e2e2e;
  color: white;
  padding: 8px;
  border-radius: 2px 0px 0px 3px;
  font-size: 13px;
  text-align: center;
  width: 40px;
  border: none;
  outline: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
`;

const Arrow = styled.div.attrs({
  className: "Arrow",
})`
  color: #008ddb;
  font-size: 18px;
  padding: 0 5px;
`;

const Badge = styled.div.attrs(props => ({
  className: 'Badge',
  'data-hovered': props.isHovered
}))`
  background: #2e2e2e;
  color: ${props => props['data-hovered'] ? 'grey' : 'white'};
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: ${props => props['data-hovered'] ? '19px' : '11px'};
  font-weight: 700;
  flex-shrink: 0;
  margin-right: 5px;
  cursor: ${props => props['data-hovered'] ? 'pointer' : 'default'};
  
  &:hover {
    color: orange;
  }
`;

const AddActionButton = styled.button.attrs({
  className: 'AddActionButton',
})`
  background: none;
  border: none;
  text-decoration: underline;
  text-underline-offset: 5px;
  padding: 5px 10px;
  color: #008ddb;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  font-family: PT Sans;
  margin: 5px 0;
  width: fit-content;
  &:hover {
    color: #0086d0;
    background: none;
  }
`;

export default SelectActions;
